import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
} from "@mui/material";
import FAQPage from "./FAQPage";
import {
  PlayArrow,
  Star,
  // Favorite,
  Share,
  Email,
  Phone,
  LocationOn,
  TrendingUp,
  Group,
  Psychology,
  Notifications,
  Person,
  Nature,
  FamilyRestroom,
  AutoAwesome,
  Celebration,
  Assessment,
  WbSunny,
  SmartToy,
  Schedule,
  CheckCircle,
  Close,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  trackEvent,
  trackConversion,
  trackModalOpen,
  trackButtonClick,
} from "../utils/analytics";
import {
  trackJourneyStep,
  trackEngagement,
  trackABTestEvent,
} from "../utils/advancedAnalytics";
import EmailCapture from "./EmailCapture";
import SEO from "./SEO";
import CustomerSupport from "./CustomerSupport";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
import AnalyticsDashboard from "./AnalyticsDashboard";
import AdminPage from "./AdminPage";
import CookieConsent from "./CookieConsent";
import PWAInstall from "./PWAInstall";
import ABTestWrapper from "./ABTestWrapper";
import EmailCapturePopup from "./EmailCapturePopup";
import Logo from "./Logo";

// How it works steps
const howItWorksSteps = [
  {
    icon: <FamilyRestroom sx={{ fontSize: 40 }} />,
    title: "Create Your Family Profile",
    description:
      "Add your kids' ages, interests, and location so we can tailor activities to you.",
  },
  {
    icon: <AutoAwesome sx={{ fontSize: 40 }} />,
    title: "Get Personalized Suggestions",
    description:
      "Receive activity ideas perfectly matched to your family and the moment.",
  },
  {
    icon: <Celebration sx={{ fontSize: 40 }} />,
    title: "Enjoy the Activities",
    description:
      "Step-by-step instructions make it easy to jump right in — no prep stress.",
  },
  {
    icon: <Assessment sx={{ fontSize: 40 }} />,
    title: "Track & Celebrate",
    description:
      "Log activities, see progress, and watch your family memories grow.",
  },
];

// Feature highlights
const features = [
  {
    icon: <Psychology />,
    title: "Smart Recommendations",
    description: "AI-powered suggestions that learn your family's preferences.",
  },
  {
    icon: <Schedule />,
    title: "Quick Activities",
    description: "Fun in just 5–15 minutes for busy days.",
  },
  {
    icon: <WbSunny />,
    title: "Weather-Aware Suggestions",
    description:
      "Get the best indoor or outdoor options based on current weather.",
  },
  {
    icon: <SmartToy />,
    title: "AI Planning Chatbot",
    description:
      "Ask for ideas in real time — just tell it your mood and time.",
  },
  {
    icon: <TrendingUp />,
    title: "Activity Tracking & Stats",
    description: "See how much quality time you're building over time.",
  },
  {
    icon: <Group />,
    title: "Family Profiles",
    description: "Activities customized for each child in your family.",
  },
];

// Testimonials
const testimonials = [
  {
    name: "Michael Rodriguez",
    location: "New York, NY",
    role: "Father of 2",
    text: "Keep Kid Busy completely transformed our weekends. My 6-year-old daughter and 4-year-old son now beg me to do fun activities instead of watching TV. We've built a treehouse, created a backyard obstacle course, and even started a family band. The activities are so well thought out - they're educational, fun, and actually doable for busy parents like me.",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    activities: "50+ activities completed",
    timeSaved: "3 hours per week",
  },
  {
    name: "David Chen",
    location: "San Francisco, CA",
    role: "Tech Executive & Father of 3",
    text: "As a busy startup founder, I was struggling to find quality time with my kids. Keep Kid Busy's AI suggestions are spot-on - they know exactly what will work for my family's schedule and my kids' ages. We've done everything from 15-minute science experiments to epic weekend camping trips. My wife says I'm a different parent now.",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    activities: "75+ activities completed",
    timeSaved: "5 hours per week",
  },
  {
    name: "James Thompson",
    location: "Austin, TX",
    role: "Father of 1",
    text: "I was worried about being a good parent after my divorce. Keep Kid Busy gave me the confidence and ideas I needed. My 8-year-old son and I now have 'Fun Fridays' where we try new activities. We've learned to cook together, built a robot, and even started a YouTube channel. The app doesn't just give you activities - it helps you become a better parent.",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
    activities: "40+ activities completed",
    timeSaved: "4 hours per week",
  },
];

const LandingPage: React.FC = () => {
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Track page view and start journey
    trackJourneyStep("landing_page_mounted", {
      page: window.location.pathname,
      referrer: document.referrer,
    });

    // Track scroll depth
    const trackScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;

      if (scrollPercent >= 25) trackEngagement("scroll_25_percent");
      if (scrollPercent >= 50) trackEngagement("scroll_50_percent");
      if (scrollPercent >= 75) trackEngagement("scroll_75_percent");
      if (scrollPercent >= 100) trackEngagement("scroll_100_percent");
    };

    window.addEventListener("scroll", trackScroll);

    // Track session end when user leaves
    const handleBeforeUnload = () => {
      trackEngagement("session_end", {
        time_on_page: Date.now() - new Date().getTime(),
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Show email popup after 5 seconds
    const popupTimer = setTimeout(() => {
      const hasSeenPopup = localStorage.getItem("email-popup-dismissed");
      if (!hasSeenPopup) {
        setShowEmailPopup(true);
      }
    }, 5000);

    return () => {
      window.removeEventListener("scroll", trackScroll);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      clearTimeout(popupTimer);
    };
  }, []);

  useEffect(() => {
    // Update time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  const handleStartJourney = () => {
    trackJourneyStep("journey_started", {
      step: "landing_page_cta",
      timestamp: new Date().toISOString(),
    });
    trackConversion("journey_started", {
      conversionType: "journey_started",
      properties: {
        step: "landing_page_cta",
        timestamp: new Date().toISOString(),
      },
    });
    setShowEmailCapture(true);
  };

  const handleEmailPopupClose = () => {
    setShowEmailPopup(false);
    localStorage.setItem("email-popup-dismissed", Date.now().toString());
  };

  const handleEmailPopupSubmit = async (email: string) => {
    try {
      // Call our backend API
      const response = await fetch(
        "http://localhost:3001/api/email/subscribe-test",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            firstName: "", // We can add a name field later
            interests: ["general"], // Default interests
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        // console.log("Email subscription successful:", data);

        trackConversion("email_captured", {
          conversionType: "email_captured",
          properties: {
            email,
            source: "popup_modal",
            userId: data.userId,
          },
        });
      } else {
        // console.error("Email subscription failed:", response.status);
      }
    } catch (error) {
      // console.error("Error submitting email:", error);
    }

    setShowEmailPopup(false);
    localStorage.setItem("email-popup-dismissed", Date.now().toString());
  };

  const handleCookieAccept = () => {
    trackEvent("cookie_consent", { action: "accepted" });
  };

  const handleCookieDecline = () => {
    trackEvent("cookie_consent", { action: "declined" });
  };

  const handleCookieSettings = () => {
    trackEvent("cookie_consent", { action: "settings" });
    // Could open a cookie settings modal here
  };

  const handleFAQOpen = () => {
    trackButtonClick("faq_opened", {
      button_text: "FAQ",
      source: "footer",
    });
    setShowFAQ(true);
  };

  const handleFAQClose = () => {
    setShowFAQ(false);
  };

  const handleAnalyticsOpen = () => {
    trackButtonClick("analytics_opened", {
      button_text: "Analytics",
      source: "footer",
    });
    setShowAnalytics(true);
  };

  const handleAnalyticsClose = () => {
    setShowAnalytics(false);
  };

  const handleAdminOpen = () => {
    trackButtonClick("admin_opened", {
      button_text: "Admin",
      source: "footer",
    });
    setShowAdmin(true);
  };

  const handleAdminClose = () => {
    setShowAdmin(false);
  };

  return (
    <>
      <SEO
        title="Keep Kid Busy - Fun Activities for Kids"
        description="Discover fun, age-appropriate activities tailored to your family's schedule, interests, and even the weather — so you can keep your kids engaged and happy."
        keywords={[
          "dad activities",
          "family bonding",
          "father child relationship",
          "family fun",
          "parenting tips",
          "kids activities",
          "family time",
        ]}
        image="/og-image.jpg"
        url="https://keepkidbusy.com"
        type="website"
      />
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        {/* Header */}
        <Box
          component="header"
          sx={{
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 1000,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 2,
              }}
            >
              <Logo size="large" color="primary" />
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    trackModalOpen("demo");
                    setShowDemo(true);
                  }}
                >
                  Watch Demo
                </Button>
                <Button
                  variant="contained"
                  onClick={handleStartJourney}
                  sx={{ fontWeight: 600 }}
                >
                  Try it Free
                </Button>
              </Box>
              {/* Mobile menu button */}
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <Button
                  variant="contained"
                  onClick={handleStartJourney}
                  sx={{ fontWeight: 600, minWidth: "auto", px: 2 }}
                >
                  Start
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* 1. Hero Section */}
        <Box
          component="section"
          sx={{
            background:
              "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 40%, #4ECDC4 70%, #45B7AA 100%)",
            color: "white",
            py: { xs: 8, md: 12 },
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 0%, transparent 40%),
                radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 40%),
                radial-gradient(circle at 40% 80%, rgba(255,255,255,0.08) 0%, transparent 40%),
                radial-gradient(circle at 90% 70%, rgba(255,255,255,0.12) 0%, transparent 40%)
              `,
              pointerEvents: "none",
            },
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              {/* Left side - Text */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Typography
                    variant="h2"
                    component="h2"
                    sx={{
                      fontWeight: 800,
                      mb: 3,
                      fontSize: { xs: "2.5rem", md: "3.5rem" },
                      lineHeight: 1.1,
                    }}
                  >
                    Bond Better with Your Kids
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      mb: 4,
                      opacity: 0.95,
                      lineHeight: 1.6,
                      textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    }}
                  >
                    Discover fun, age-appropriate activities tailored to your
                    family's schedule, interests, and even the weather — so you
                    can make the most of every moment.
                  </Typography>

                  {/* CTA Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1, sm: 2 },
                      flexWrap: "wrap",
                      mb: 3,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <ABTestWrapper testId="hero_cta_test">
                      {(variantId, config) => (
                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => {
                            handleStartJourney();
                            if (variantId) {
                              trackABTestEvent(
                                "hero_cta_test",
                                variantId,
                                "click",
                                {
                                  button_text:
                                    config?.buttonText || "Try it Free",
                                }
                              );
                            }
                          }}
                          startIcon={<CheckCircle />}
                          sx={{
                            background:
                              "linear-gradient(45deg, #FF6B6B, #FF8E53)",
                            color: "white",
                            fontWeight: 600,
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1.25, sm: 1.5 },
                            borderRadius: 3,
                            boxShadow: "0 8px 25px rgba(255,107,107,0.3)",
                            width: { xs: "100%", sm: "auto" },
                            "&:hover": {
                              background:
                                "linear-gradient(45deg, #FF8E53, #FFD93D)",
                              boxShadow: "0 12px 35px rgba(255,142,83,0.4)",
                              transform: "translateY(-2px) scale(1.03)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          {config?.buttonText || "Try it Free"}
                        </Button>
                      )}
                    </ABTestWrapper>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => {
                        trackModalOpen("demo");
                        setShowDemo(true);
                      }}
                      startIcon={<PlayArrow />}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.2))",
                        border: "2px solid rgba(255,255,255,0.3)",
                        color: "white",
                        px: { xs: 3, sm: 4 },
                        py: { xs: 1.25, sm: 1.5 },
                        borderRadius: 3,
                        backdropFilter: "blur(10px)",
                        width: { xs: "100%", sm: "auto" },
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.3))",
                          border: "2px solid rgba(255,255,255,0.5)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Watch Demo
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              {/* Right side - Visual */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <Card
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.25))",
                      backdropFilter: "blur(25px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow:
                        "0 25px 50px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)",
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                          "radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)",
                        pointerEvents: "none",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 0, position: "relative" }}>
                      {/* App Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 2,
                          borderBottom: "1px solid rgba(255,255,255,0.2)",
                          background:
                            "linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "white",
                            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                          }}
                        >
                          <Logo size="small" color="white" showText={false} />
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            sx={{
                              color: "white",
                              background: "rgba(255,255,255,0.1)",
                              "&:hover": {
                                background: "rgba(255,255,255,0.2)",
                              },
                            }}
                          >
                            <Notifications />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{
                              color: "white",
                              background: "rgba(255,255,255,0.1)",
                              "&:hover": {
                                background: "rgba(255,255,255,0.2)",
                              },
                            }}
                          >
                            <Person />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* App Content */}
                      <Box sx={{ p: 3 }}>
                        {/* Time and Date */}
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: 700,
                              color: "white",
                              mb: 0.5,
                              textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            }}
                          >
                            {formatTime(currentTime)}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "rgba(255,255,255,0.8)",
                              textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                            }}
                          >
                            {formatDate(currentTime)}
                          </Typography>
                        </Box>

                        {/* Quick Activity */}
                        <Card
                          sx={{
                            background:
                              "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.3))",
                            mb: 3,
                            borderRadius: 2,
                            border: "1px solid rgba(255,255,255,0.2)",
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  background:
                                    "linear-gradient(45deg, #4CAF50, #66BB6A)",
                                  borderRadius: "50%",
                                  width: 24,
                                  height: 24,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mr: 1,
                                  boxShadow: "0 2px 4px rgba(76,175,80,0.3)",
                                }}
                              >
                                <Nature sx={{ fontSize: 16, color: "white" }} />
                              </Box>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: "rgba(255,255,255,0.95)",
                                  fontWeight: 600,
                                }}
                              >
                                Quick Activity
                              </Typography>
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{
                                color: "white",
                                fontWeight: 600,
                                mb: 1,
                                textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                              }}
                            >
                              Backyard Treasure Hunt
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Chip
                                label="20 min"
                                size="small"
                                sx={{
                                  background:
                                    "linear-gradient(45deg, rgba(255,255,255,0.3), rgba(255,255,255,0.4))",
                                  color: "white",
                                  fontSize: "0.75rem",
                                  border: "1px solid rgba(255,255,255,0.2)",
                                }}
                              />
                              <Chip
                                label="Ages 4-10"
                                size="small"
                                sx={{
                                  background:
                                    "linear-gradient(45deg, rgba(255,255,255,0.3), rgba(255,255,255,0.4))",
                                  color: "white",
                                  fontSize: "0.75rem",
                                  border: "1px solid rgba(255,255,255,0.2)",
                                }}
                              />
                            </Box>
                          </CardContent>
                        </Card>

                        {/* Weekly Goal */}
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: "rgba(255,255,255,0.95)",
                              fontWeight: 600,
                              mb: 1,
                            }}
                          >
                            This Week's Goal
                          </Typography>
                          <Box
                            sx={{
                              background: "rgba(255,255,255,0.2)",
                              borderRadius: 1,
                              height: 8,
                              mb: 1,
                              overflow: "hidden",
                              border: "1px solid rgba(255,255,255,0.1)",
                            }}
                          >
                            <Box
                              sx={{
                                background:
                                  "linear-gradient(90deg, #f97316, #ea580c)",
                                height: "100%",
                                width: "75%",
                                borderRadius: 1,
                                boxShadow: "0 0 10px rgba(249,115,22,0.5)",
                              }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255,255,255,0.8)",
                            }}
                          >
                            3 of 4 activities completed
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* 2. Problem Statement */}
        <Box component="section" sx={{ py: 8, bgcolor: "#FAFAFA" }}>
          <Container maxWidth="md">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h3"
                component="h3"
                align="center"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  background: "linear-gradient(45deg, #FF6B6B, #FF8E53)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Time is precious — make it count.
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.6,
                  maxWidth: 800,
                  mx: "auto",
                }}
              >
                As parents, we want to spend quality time with our kids — but
                busy schedules, endless options, and tired brains make it hard
                to decide what to do.
                <br />
                <strong>
                  Keep Kid Busy takes away the guesswork and puts meaningful,
                  fun activities at your fingertips.
                </strong>
              </Typography>
            </motion.div>
          </Container>
        </Box>

        {/* 3. How It Works */}
        <Box component="section" sx={{ py: 8 }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h3"
                component="h3"
                align="center"
                sx={{
                  fontWeight: 700,
                  mb: 6,
                  background: "linear-gradient(45deg, #4ECDC4, #45B7AA)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                From "What should we do?" to "That was amazing!" — in minutes.
              </Typography>
            </motion.div>

            <Grid container spacing={4}>
              {howItWorksSteps.map((step, index) => (
                <Grid item xs={12} sm={6} md={3} key={step.title}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      sx={{
                        height: 280,
                        textAlign: "center",
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition:
                          "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: "primary.main",
                          mb: 2,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {step.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        component="h4"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {step.description}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* 4. Feature Highlights */}
        <Box component="section" sx={{ py: 8, bgcolor: "#FAFAFA" }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h3"
                component="h3"
                align="center"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: "linear-gradient(45deg, #FF6B6B, #FF8E53)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Everything You Need for Meaningful Moments
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "text.secondary",
                  mb: 6,
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                Powerful features designed to make family time effortless and
                fun
              </Typography>
            </motion.div>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={feature.title}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      sx={{
                        height: 320,
                        display: "flex",
                        flexDirection: "column",
                        transition:
                          "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                          p: 3,
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: "primary.light",
                            color: "primary.main",
                            borderRadius: "50%",
                            width: 80,
                            height: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography
                          variant="h6"
                          component="h4"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                        >
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* 5. Screenshots / Product Preview */}
        <Box component="section" sx={{ py: 8 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid
                item
                xs={12}
                md={6}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  style={{ maxWidth: "500px" }}
                >
                  <Typography
                    variant="h3"
                    component="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      background: "linear-gradient(45deg, #4ECDC4, #45B7AA)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    A Beautiful, Simple Experience
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "text.secondary",
                      mb: 3,
                      lineHeight: 1.6,
                    }}
                  >
                    Keep Kid Busy is designed to be as intuitive as it is
                    powerful — so you spend less time scrolling and more time
                    playing.
                  </Typography>
                  <Box component="ul" sx={{ pl: 3 }}>
                    <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                      Activity list with smart filters
                    </Typography>
                    <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                      AI chat interface for instant ideas
                    </Typography>
                    <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                      Step-by-step activity instructions
                    </Typography>
                    <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                      Progress tracking and celebrations
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 300,
                        height: 400,
                        borderRadius: 4,
                        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                        background: "linear-gradient(135deg, #4ECDC4, #45B7AA)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Box
                        component="img"
                        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='2' y='3' width='20' height='14' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='8' y1='21' x2='16' y2='21'%3E%3C/line%3E%3Cline x1='12' y1='17' x2='12' y2='21'%3E%3C/line%3E%3C/svg%3E"
                        alt="App preview"
                        sx={{
                          width: 80,
                          height: 80,
                          opacity: 0.8,
                        }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        App Screenshot
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.8, textAlign: "center" }}
                      >
                        Coming Soon
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* 6. Social Proof Statistics */}
        <Box component="section" sx={{ py: 8, bgcolor: "#FAFAFA" }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h3"
                component="h3"
                align="center"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: "linear-gradient(45deg, #FF6B6B, #FF8E53)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Trusted by Parents Worldwide
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "text.secondary",
                  mb: 6,
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                Join thousands of parents who are already creating amazing
                memories with their kids
              </Typography>
            </motion.div>

            <Grid container spacing={4}>
              <Grid item xs={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 3,
                      height: 200,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      background: "linear-gradient(135deg, #4ECDC4, #44A08D)",
                      color: "white",
                      borderRadius: 3,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                      },
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                        fontSize: { xs: "2rem", md: "3rem" },
                      }}
                    >
                      15,000+
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        opacity: 0.9,
                      }}
                    >
                      Active Parents
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.8,
                        fontSize: "0.9rem",
                      }}
                    >
                      Building stronger bonds every day
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 3,
                      height: 200,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      background: "linear-gradient(135deg, #FF6B6B, #FF8E53)",
                      color: "white",
                      borderRadius: 3,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                      },
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                        fontSize: { xs: "2rem", md: "3rem" },
                      }}
                    >
                      50,000+
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        opacity: 0.9,
                      }}
                    >
                      Activities Completed
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.8,
                        fontSize: "0.9rem",
                      }}
                    >
                      From quick games to epic adventures
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 3,
                      height: 200,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      background: "linear-gradient(135deg, #FFD93D, #FFB347)",
                      color: "white",
                      borderRadius: 3,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                      },
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                        fontSize: { xs: "2rem", md: "3rem" },
                      }}
                    >
                      2.5M+
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        opacity: 0.9,
                      }}
                    >
                      Quality Minutes
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.8,
                        fontSize: "0.9rem",
                      }}
                    >
                      Spent bonding with kids
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 3,
                      height: 200,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      background: "linear-gradient(135deg, #9B59B6, #8E44AD)",
                      color: "white",
                      borderRadius: 3,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-8px)",
                      },
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                        fontSize: { xs: "2rem", md: "3rem" },
                      }}
                    >
                      4.9/5
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        opacity: 0.9,
                      }}
                    >
                      User Rating
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.8,
                        fontSize: "0.9rem",
                      }}
                    >
                      From thousands of reviews
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* 7. Trust Badges & Security */}
        <Box component="section" sx={{ py: 6, bgcolor: "white" }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h5"
                component="h3"
                align="center"
                sx={{
                  fontWeight: 600,
                  mb: 4,
                  color: "#2C3E50",
                }}
              >
                Your Family's Security & Privacy is Our Priority
              </Typography>
            </motion.div>

            <Grid
              container
              spacing={3}
              justifyContent="center"
              alignItems="center"
            >
              <Grid item xs={6} sm={4} md={2}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      height: 120,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "linear-gradient(135deg, #4CAF50, #45A049)",
                      color: "white",
                      borderRadius: 2,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                        }}
                      >
                        🔒
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8rem",
                      }}
                    >
                      SSL Secured
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      height: 120,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "linear-gradient(135deg, #2196F3, #1976D2)",
                      color: "white",
                      borderRadius: 2,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                        }}
                      >
                        🛡️
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8rem",
                      }}
                    >
                      GDPR Compliant
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      height: 120,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "linear-gradient(135deg, #FF9800, #F57C00)",
                      color: "white",
                      borderRadius: 2,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                        }}
                      >
                        🔐
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8rem",
                      }}
                    >
                      Data Encrypted
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      height: 120,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "linear-gradient(135deg, #9C27B0, #7B1FA2)",
                      color: "white",
                      borderRadius: 2,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                        }}
                      >
                        ✅
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8rem",
                      }}
                    >
                      COPPA Safe
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      height: 120,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "linear-gradient(135deg, #E91E63, #C2185B)",
                      color: "white",
                      borderRadius: 2,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                        }}
                      >
                        🏆
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8rem",
                      }}
                    >
                      Award Winning
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      height: 120,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "linear-gradient(135deg, #607D8B, #455A64)",
                      color: "white",
                      borderRadius: 2,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                        }}
                      >
                        🌟
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8rem",
                      }}
                    >
                      Expert Verified
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>

            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.9rem",
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                Keep Kid Busy meets the highest standards for family safety and
                data protection. Your information is encrypted, secure, and
                never shared with third parties.
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* 8. Testimonials */}
        <Box component="section" sx={{ py: 8, bgcolor: "#FAFAFA" }}>
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h3"
                component="h3"
                align="center"
                sx={{
                  fontWeight: 700,
                  mb: 6,
                  background: "linear-gradient(45deg, #FF6B6B, #FF8E53)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Loved by Busy Parents Everywhere
              </Typography>
            </motion.div>

            <Grid container spacing={4}>
              {testimonials.map((testimonial, index) => (
                <Grid item xs={12} md={4} key={testimonial.name}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      sx={{
                        height: { xs: 480, sm: 500, md: 520 },
                        p: { xs: 3, sm: 3.5, md: 4 },
                        textAlign: "left",
                        borderRadius: 3,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all 0.3s ease-in-out",
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
                        border: "1px solid rgba(0,0,0,0.08)",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                          border: "1px solid rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      <Box>
                        {/* Rating */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: 3,
                          }}
                        >
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              sx={{
                                color:
                                  i < testimonial.rating
                                    ? "#FFD700"
                                    : "#E0E0E0",
                                fontSize: 24,
                                mr: 0.5,
                              }}
                            />
                          ))}
                        </Box>

                        {/* Testimonial Text */}
                        <Typography
                          variant="body2"
                          sx={{
                            fontStyle: "italic",
                            mb: 3,
                            lineHeight: 1.5,
                            color: "#2C3E50",
                            fontSize: {
                              xs: "0.85rem",
                              sm: "0.875rem",
                              md: "0.9rem",
                            },
                            fontWeight: 400,
                            textAlign: "left",
                            flexGrow: 1,
                          }}
                        >
                          "{testimonial.text}"
                        </Typography>

                        {/* Stats */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            mb: 3,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            label={testimonial.activities}
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              background:
                                "linear-gradient(45deg, #4ECDC4, #44A08D)",
                              color: "white",
                              border: "none",
                              "&:hover": {
                                background:
                                  "linear-gradient(45deg, #44A08D, #4ECDC4)",
                              },
                            }}
                          />
                          <Chip
                            label={testimonial.timeSaved}
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              background:
                                "linear-gradient(45deg, #FF6B6B, #FF8E53)",
                              color: "white",
                              border: "none",
                              "&:hover": {
                                background:
                                  "linear-gradient(45deg, #FF8E53, #FF6B6B)",
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Author Info */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          borderTop: "2px solid",
                          borderColor: "rgba(0,0,0,0.06)",
                          pt: 2,
                          mt: "auto",
                        }}
                      >
                        <Avatar
                          src={testimonial.avatar}
                          sx={{
                            mr: { xs: 2, sm: 2.5, md: 3 },
                            width: { xs: 50, sm: 55, md: 60 },
                            height: { xs: 50, sm: 55, md: 60 },
                            border: "3px solid",
                            borderColor: "#4ECDC4",
                            boxShadow: "0 4px 12px rgba(78, 205, 196, 0.3)",
                          }}
                        />
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              mb: 0.5,
                              color: "#2C3E50",
                              fontSize: {
                                xs: "0.95rem",
                                sm: "1rem",
                                md: "1.1rem",
                              },
                            }}
                          >
                            {testimonial.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              mb: 0.5,
                              color: "#4ECDC4",
                              fontSize: "0.9rem",
                            }}
                          >
                            {testimonial.role}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#7F8C8D",
                              fontSize: "0.85rem",
                            }}
                          >
                            {testimonial.location}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* 8. Enhanced Final CTA Section */}

        {/* 8. Enhanced Final CTA Section */}
        <Box
          component="section"
          sx={{
            background: "linear-gradient(45deg, #FF6B6B, #FF8E53, #4ECDC4)",
            color: "white",
            py: { xs: 8, md: 12 },
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1) 0%, transparent 40%),
                radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 40%),
                radial-gradient(circle at 40% 80%, rgba(255,255,255,0.06) 0%, transparent 40%)
              `,
              pointerEvents: "none",
            },
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {/* Urgency Badge */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Chip
                  label="🎉 Limited Time: Free Premium Access"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    py: 1,
                    px: 2,
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.3)",
                  }}
                />
              </Box>

              <Typography
                variant="h2"
                component="h3"
                align="center"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  lineHeight: 1.2,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                Start Creating
                <br />
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(45deg, #FFD93D, #FFB347)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Unforgettable Memories
                </Box>
              </Typography>

              <Typography
                variant="h5"
                align="center"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  maxWidth: 700,
                  mx: "auto",
                  lineHeight: 1.4,
                  fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" },
                }}
              >
                Join 15,000+ dads who are already building stronger bonds with
                their kids. It only takes 2 minutes to set up — and the next
                activity could become your family's favorite tradition.
              </Typography>

              {/* Social Proof Stats */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: { xs: 2, md: 4 },
                  mb: 4,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#FFD93D",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                      fontSize: { xs: "1.75rem", sm: "2rem", md: "2.125rem" },
                    }}
                  >
                    15,000+
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: "0.9rem",
                    }}
                  >
                    Active Dads
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#4ECDC4",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                      fontSize: { xs: "1.75rem", sm: "2rem", md: "2.125rem" },
                    }}
                  >
                    4.9/5
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: "0.9rem",
                    }}
                  >
                    User Rating
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#FF8E53",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                      fontSize: { xs: "1.75rem", sm: "2rem", md: "2.125rem" },
                    }}
                  >
                    2.5M+
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: "0.9rem",
                    }}
                  >
                    Quality Minutes
                  </Typography>
                </Box>
              </Box>

              {/* Multiple CTA Buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  gap: 2,
                  mb: 4,
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStartJourney}
                  startIcon={<CheckCircle />}
                  sx={{
                    bgcolor: "white",
                    color: "#FF6B6B",
                    fontWeight: 700,
                    px: { xs: 3, sm: 4, md: 5 },
                    py: { xs: 1.5, sm: 1.75, md: 2 },
                    fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                    borderRadius: 3,
                    textTransform: "none",
                    boxShadow: "0 8px 25px rgba(255,255,255,0.3)",
                    "&:hover": {
                      bgcolor: "#FAFAFA",
                      transform: "translateY(-3px)",
                      boxShadow: "0 12px 35px rgba(255,255,255,0.4)",
                    },
                    transition: "all 0.3s ease",
                    minWidth: { xs: 180, sm: 200 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Get Started – It's Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    trackButtonClick("final_cta_secondary", {
                      button_text: "Watch Demo",
                    });
                    setShowDemo(true);
                  }}
                  startIcon={<PlayArrow />}
                  sx={{
                    borderColor: "rgba(255,255,255,0.8)",
                    color: "white",
                    fontWeight: 600,
                    px: { xs: 3, sm: 3.5, md: 4 },
                    py: { xs: 1.5, sm: 1.75, md: 2 },
                    fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                    borderRadius: 3,
                    textTransform: "none",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    minWidth: { xs: 140, sm: 160 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Watch Demo
                </Button>
              </Box>

              {/* Trust Indicators */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: { xs: 1, md: 3 },
                  mb: 3,
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    opacity: 0.9,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: "1.2rem",
                    }}
                  >
                    🔒
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.85rem",
                    }}
                  >
                    No Credit Card
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    opacity: 0.9,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: "1.2rem",
                    }}
                  >
                    ⚡
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.85rem",
                    }}
                  >
                    2-Minute Setup
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    opacity: 0.9,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: "1.2rem",
                    }}
                  >
                    🎯
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.85rem",
                    }}
                  >
                    Cancel Anytime
                  </Typography>
                </Box>
              </Box>

              {/* Urgency Message */}
              <Typography
                variant="body2"
                align="center"
                sx={{
                  opacity: 0.8,
                  fontStyle: "italic",
                  fontSize: "0.9rem",
                }}
              >
                ⏰ Join thousands of dads who are already creating amazing
                memories. Start your journey today!
              </Typography>
            </motion.div>
          </Container>
        </Box>

        {/* 8. Footer */}
        <Box
          component="footer"
          sx={{
            bgcolor: "#1A1A1A",
            color: "white",
            py: 6,
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Logo size="large" color="white" />
                <Typography
                  variant="body2"
                  sx={{ mb: 2, lineHeight: 1.6, opacity: 0.8 }}
                >
                  Helping parents create meaningful connections with their kids
                  through fun activities and quality time together.
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    size="small"
                    sx={{ color: "white", opacity: 0.8 }}
                    onClick={() =>
                      trackEvent("social_click", { platform: "facebook" })
                    }
                  >
                    <Share />
                  </IconButton>
                </Box>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography
                  variant="h6"
                  component="h5"
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Links
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant="text"
                    onClick={handleFAQOpen}
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      textDecoration: "none",
                      "&:hover": { color: "white", background: "transparent" },
                      p: 0,
                      minWidth: "auto",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      justifyContent: "flex-start",
                    }}
                  >
                    FAQ
                  </Button>
                  <Button
                    variant="text"
                    onClick={handleAnalyticsOpen}
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      textDecoration: "none",
                      "&:hover": { color: "white", background: "transparent" },
                      p: 0,
                      minWidth: "auto",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      justifyContent: "flex-start",
                    }}
                  >
                    Analytics
                  </Button>
                  <Button
                    variant="text"
                    onClick={handleAdminOpen}
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      textDecoration: "none",
                      "&:hover": { color: "white", background: "transparent" },
                      p: 0,
                      minWidth: "auto",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      justifyContent: "flex-start",
                    }}
                  >
                    Admin
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setShowTermsOfService(true)}
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      textDecoration: "none",
                      "&:hover": { color: "white", background: "transparent" },
                      p: 0,
                      minWidth: "auto",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      justifyContent: "flex-start",
                    }}
                  >
                    Terms of Service
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setShowPrivacyPolicy(true)}
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      textDecoration: "none",
                      "&:hover": { color: "white", background: "transparent" },
                      p: 0,
                      minWidth: "auto",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      justifyContent: "flex-start",
                    }}
                  >
                    Privacy Policy
                  </Button>
                  <Typography
                    variant="body2"
                    component="a"
                    href="mailto:hello@keepkidbusy.com"
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      textDecoration: "none",
                      "&:hover": { color: "white" },
                    }}
                  >
                    Contact
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  variant="h6"
                  component="h5"
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Contact Info
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Email
                      sx={{ fontSize: 16, color: "rgba(255,255,255,0.8)" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      hello@keepkidbusy.com
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Phone
                      sx={{ fontSize: 16, color: "rgba(255,255,255,0.8)" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      +1 (415) 555-0123
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationOn
                      sx={{ fontSize: 16, color: "rgba(255,255,255,0.8)" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      San Francisco, CA 94102
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  variant="h6"
                  component="h5"
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Follow Us
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: "rgba(255,255,255,0.8)" }}
                >
                  Stay connected for tips and updates.
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    size="small"
                    sx={{ color: "rgba(255,255,255,0.8)" }}
                    onClick={() =>
                      trackEvent("social_click", { platform: "instagram" })
                    }
                  >
                    <Share />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: "rgba(255,255,255,0.8)" }}
                    onClick={() =>
                      trackEvent("social_click", { platform: "facebook" })
                    }
                  >
                    <Share />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: "rgba(255,255,255,0.8)" }}
                    onClick={() =>
                      trackEvent("social_click", { platform: "linkedin" })
                    }
                  >
                    <Share />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
            <Box
              sx={{
                borderTop: 1,
                borderColor: "rgba(255,255,255,0.2)",
                mt: 4,
                pt: 4,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                © 2025 Keep Kid Busy. All rights reserved.
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Customer Support Floating Button */}
        <CustomerSupport />

        {/* Cookie Consent Banner */}
        <CookieConsent
          onAccept={handleCookieAccept}
          onDecline={handleCookieDecline}
          onSettings={handleCookieSettings}
        />

        {/* PWA Install Prompt */}
        <PWAInstall />

        {/* Email Capture Dialog */}
        <EmailCapture
          open={showEmailCapture}
          onClose={() => setShowEmailCapture(false)}
        />

        {/* Email Capture Popup */}
        {showEmailPopup && (
          <EmailCapturePopup
            onClose={handleEmailPopupClose}
            onEmailSubmit={handleEmailPopupSubmit}
          />
        )}

        {/* Privacy Policy */}
        {showPrivacyPolicy && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1400,
              bgcolor: "background.default",
              overflow: "auto",
            }}
          >
            <PrivacyPolicy onBack={() => setShowPrivacyPolicy(false)} />
          </Box>
        )}

        {/* Terms of Service */}
        {showTermsOfService && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1400,
              bgcolor: "background.default",
              overflow: "auto",
            }}
          >
            <TermsOfService onBack={() => setShowTermsOfService(false)} />
          </Box>
        )}

        {/* FAQ Page */}
        {showFAQ && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1400,
              bgcolor: "background.default",
              overflow: "auto",
            }}
          >
            <FAQPage onBack={handleFAQClose} />
          </Box>
        )}

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1400,
              bgcolor: "background.default",
              overflow: "auto",
            }}
          >
            <Box sx={{ position: "relative" }}>
              <IconButton
                onClick={handleAnalyticsClose}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 1,
                  bgcolor: "background.paper",
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "background.paper",
                  },
                }}
              >
                <Close />
              </IconButton>
              <AnalyticsDashboard />
            </Box>
          </Box>
        )}

        {/* Admin Dashboard */}
        {showAdmin && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1400,
              bgcolor: "background.default",
              overflow: "auto",
            }}
          >
            <AdminPage onBack={handleAdminClose} />
          </Box>
        )}

        {/* Demo Dialog */}
        <Dialog
          open={showDemo}
          onClose={() => setShowDemo(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              See Keep Kid Busy in Action
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 300,
                bgcolor: "grey.100",
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <PlayArrow sx={{ fontSize: 80, color: "primary.main" }} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Demo Video Coming Soon
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We're working on an amazing demo video to show you exactly how
                  Keep Kid Busy works.
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              Keep Kid Busy is designed to make it easy for busy parents to find
              and plan fun activities with their kids. Our platform provides:
            </Typography>
            <Box component="ul" sx={{ mt: 2, pl: 3 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Curated activity ideas for different age groups and interests
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Step-by-step instructions and materials lists
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Progress tracking and milestone celebrations
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Community support and tips from other dads
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDemo(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={handleStartJourney}
              sx={{ fontWeight: 600 }}
            >
              Try it Free
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default LandingPage;
